import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm } from 'react-hook-form';
import { isEqual } from 'lodash';
import { RunLatestQueue } from '@async/run-latest-queue';
import { ApiResult } from '../../api';
import { useToast, TOAST_INCOMPLETE_INFO_ERROR_MESSAGE } from '../../Toast';
import { useRefInView } from '../../hooks';
import { useOffer } from '../../offer';
import { useReporter } from '../../reporter';
import {
  BillingInformationForm,
  BillingInformationSchema,
  ShippingAddressWithNameSchema,
} from '../../components';
import { CheckoutBar } from '../CheckoutBar';
import { CheckoutInformation } from '../CheckoutInformation';
import { schema as AccountInfoSchema } from '../AccountInfoForm/schema';
import { ShippingAddressForm } from '../ShippingAddressForm';
import { AccountInfoForm } from '../AccountInfoForm';
import {
  PAYMENT_ERROR_MESSAGE,
  PAYMENT_INSUFFICIENT_FUNDS_ERROR_MESSAGE,
  ADDRESS_ERROR_MESSAGE,
  PASSWORD_ERROR_MESSAGE,
} from '../constants';
import {
  StyledCheckoutContainer,
  StyledCheckoutForm,
  ShippingAddressHeader,
  AccountInfoHeader,
  BillingInfoHeader,
} from './styles.js';
import {
  checkoutButtonClicked,
  duplicateEmailEntered,
  viewCheckoutPageEvent,
} from './events.js';
import {
  getCheckoutBarProps,
  getCheckoutInformationProps,
} from './propBuilders';

const shippingAddressFields = Object.keys(ShippingAddressWithNameSchema.fields);
const genericInvalidAddressMessage =
  'This address is invalid. Please review and update the address';

export const CheckoutForm = ({
  headerShippingAddress,
  labelFirstName,
  labelLastName,
  labelAddress,
  labelApt,
  labelZipCode,
  labelCity,
  stateOptions,
  labelState,
  labelPhoneNumber,
  labelSMSUpdates,
  labelSMSTerms,
  headerAccountInfo,
  labelEmail,
  labelPassword,
  labelConfirmation,
  headlineBillingInformation,
  labelWithStripe,
  labelNameCard,
  labelCardNumber,
  labelExpirationDate,
  labelCVC,
  placeholderNameCard,
  placeholderCardNumber,
  placeholderExpirationDate,
  placeholderCVC,
  orderSummary,
  recurringOrders,
  ctaText,
  onSubmit,
  customer,
  products,
  subscriptionProducts,
  address,
  updateAddress,
  cart,
  isValidating,
  refreshCart,
  deletePet,
  pets,
  period,
  showAddons,
  showPromoCode,
  applyPromoCode,
  removePromoCode,
  isDefaultPromoCode,
  currentOfferCode,
  defaultOfferCode,
}) => {
  const [refInView, ref] = useRefInView(true);
  const reporter = useReporter();
  const offer = useOffer();
  const { showToast } = useToast();
  const queue = useRef(new RunLatestQueue(200));
  useEffect(() => {
    reporter.tag(viewCheckoutPageEvent());
  }, [reporter]);

  const methods = useForm({
    defaultValues: {
      firstName: address.first_name || customer.first_name || '',
      lastName: address.last_name || customer.last_name || '',
      email: customer?.email || '',
      smsUpdates: address.sms_updates ?? true,
      address: address.address1 || '',
      apt: address.address2 || '',
      zipCode: address.zip_code || '',
      city: address.city || '',
      state: address.state || '',
      phoneNumber: address.phone || '',
      new_password: '',
      confirm_password: '',
      name: '',
      cardNumber: '',
      cardExpiry: '',
      cardCvc: '',
    },
    resolver: yupResolver(
      ShippingAddressWithNameSchema.concat(AccountInfoSchema).concat(
        BillingInformationSchema,
      ),
    ),
    mode: 'onTouched',
  });
  const { clearErrors, formState, getValues, reset, setError, watch } = methods;
  const addressErrorMessage = formState.errors.address?.message;

  useEffect(() => {
    const sub = watch(async (values, { name }) => {
      if (!shippingAddressFields.includes(name)) {
        return;
      }

      const isValidAddress = ShippingAddressWithNameSchema.isValidSync(values);
      const payload = {
        first_name: values.firstName,
        last_name: values.lastName,
        address1: values.address,
        address2: values.apt,
        city: values.city,
        phone: values.phoneNumber,
        state: values.state,
        zip_code: values.zipCode,
      };

      if (
        isValidAddress &&
        // `id` is in address and should be part of the comparison
        (!isEqual({ ...payload, id: address.id }, address) ||
          addressErrorMessage === genericInvalidAddressMessage)
      ) {
        queue.current.add(async () => {
          const result = await updateAddress(payload);
          await ApiResult.match(result, {
            success: async () => {
              clearErrors(shippingAddressFields);
              await refreshCart({ resyncTaxes: true });
            },
            error: {
              client: ({ original }) => {
                reporter.error(original);
              },
              address: ({ fields, nonFieldErrors }) => {
                if (nonFieldErrors) {
                  setError('address', {
                    type: 'validate',
                    message: genericInvalidAddressMessage,
                  });
                }

                fields.forEach(({ name }) => {
                  setError(name, {
                    type: 'validate',
                    message: `This ${name} is invalid.`,
                  });
                });
              },
            },
          });
          ApiResult.ifError(result, () =>
            setTimeout(() => {
              reset(getValues(), {
                keepIsSubmitted: false,
                keepErrors: true,
              });
            }),
          );
        });
      }
    });
    return () => sub.unsubscribe();
  }, [
    address,
    addressErrorMessage,
    clearErrors,
    getValues,
    reporter,
    reset,
    setError,
    watch,
    updateAddress,
    refreshCart,
  ]);

  const handleSubmit = async data => {
    reporter.tag(checkoutButtonClicked());
    const result = await onSubmit(data);
    ApiResult.match(result, {
      error: {
        client: error => {
          reporter.error(error.original);
        },
        field: ({ field, message }) => {
          if (field === 'email') {
            reporter.tag(duplicateEmailEntered(message));
          }
          setError(field, { type: 'server', message }, { shouldFocus: true });
        },
        paymentInvalid: () => {
          setError(
            'cardNumber',
            { type: 'payment', message: PAYMENT_ERROR_MESSAGE },
            { shouldFocus: true },
          );
        },
        paymentInsufficientFunds: () => {
          setError(
            'cardNumber',
            {
              type: 'payment',
              message: PAYMENT_INSUFFICIENT_FUNDS_ERROR_MESSAGE,
            },
            { shouldFocus: true },
          );
        },
        unableToProcessOrder: () => {
          setError(
            'cardNumber',
            { type: 'payment', message: PAYMENT_ERROR_MESSAGE },
            { shouldFocus: true },
          );
        },
        checkoutAddress: () => {
          setError(
            'address',
            { type: 'address', message: ADDRESS_ERROR_MESSAGE },
            { shouldFocus: true },
          );
        },
        passwordNotSet: () => {
          setError(
            'new_password',
            { type: 'password', message: PASSWORD_ERROR_MESSAGE },
            { shouldFocus: true },
          );
        },
      },
    });
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
          keepErrors: true,
        }),
      ),
    );
  };

  const handleErrorToast = data => {
    showToast({
      status: 'error',
      message: TOAST_INCOMPLETE_INFO_ERROR_MESSAGE,
      headline: 'Error',
    });
    setTimeout(() => {
      reset(getValues(), {
        keepIsSubmitted: false,
        keepErrors: true,
      });
    }, 1500);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit, handleErrorToast)}>
        <StyledCheckoutContainer>
          <StyledCheckoutForm>
            <ShippingAddressHeader>
              {headerShippingAddress}
            </ShippingAddressHeader>
            <ShippingAddressForm
              labelFirstName={labelFirstName}
              labelLastName={labelLastName}
              labelAddress={labelAddress}
              labelApt={labelApt}
              labelZipCode={labelZipCode}
              labelCity={labelCity}
              stateOptions={stateOptions}
              labelState={labelState}
              labelPhoneNumber={labelPhoneNumber}
              labelSMSUpdates={labelSMSUpdates}
              labelSMSTerms={labelSMSTerms}
            />
            <AccountInfoHeader>{headerAccountInfo}</AccountInfoHeader>
            <AccountInfoForm
              labelEmail={labelEmail}
              labelPassword={labelPassword}
              labelConfirmation={labelConfirmation}
            />
            {/*
              This has been commented out for now as it will be reintroduced in the future.
              PR for reference: https://github.com/myollie/olliemain/pull/6622
              When reintroduced, must re-add:
              - imports for both components below
              - buttonAlt and buttonImage props
              - update test to check for the IDMeForm
            <IDMeHeader>{headerIDMeForm}</IDMeHeader>
            <IDMeForm
              message={message}
              buttonAlt={buttonAlt}
              buttonImage={buttonImage}
            />
            */}
            <BillingInfoHeader>{headlineBillingInformation}</BillingInfoHeader>
            <BillingInformationForm
              labelNameCard={labelNameCard}
              labelCardNumber={labelCardNumber}
              labelExpirationDate={labelExpirationDate}
              labelCVC={labelCVC}
              labelWithStripe={labelWithStripe}
              placeholderNameCard={placeholderNameCard}
              placeholderCardNumber={placeholderCardNumber}
              placeholderExpirationDate={placeholderExpirationDate}
              placeholderCVC={placeholderCVC}
            />
          </StyledCheckoutForm>
          <CheckoutInformation
            {...getCheckoutInformationProps({
              refreshCart,
              isValidating,
              cart,
              deletePet,
              pets,
              address,
              products,
              subscriptionProducts,
              orderSummary,
              formattedOfferAmount: offer.formattedAmount,
              recurringOrders,
              formState: methods.formState,
              buttonRef: ref,
              showAddons,
              period,
              currentOfferCode,
              defaultOfferCode,
            })}
            showPromoCode={showPromoCode}
            applyPromoCode={applyPromoCode}
            removePromoCode={removePromoCode}
            isDefaultPromoCode={isDefaultPromoCode}
            currentOfferCode={currentOfferCode}
          />
          <CheckoutBar
            {...getCheckoutBarProps({
              cart,
              products,
              pets,
              ctaText,
              formState: methods.formState,
              refInView,
              period,
            })}
          />
        </StyledCheckoutContainer>
      </form>
    </FormProvider>
  );
};

CheckoutForm.displayName = 'CheckoutForm';
CheckoutForm.propTypes = {
  headerShippingAddress: PropTypes.string.isRequired,
  labelFirstName: PropTypes.string.isRequired,
  labelLastName: PropTypes.string.isRequired,
  labelAddress: PropTypes.string.isRequired,
  labelApt: PropTypes.string.isRequired,
  labelZipCode: PropTypes.string.isRequired,
  labelCity: PropTypes.string.isRequired,
  labelState: PropTypes.string.isRequired,
  stateOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }).isRequired,
  ),
  labelPhoneNumber: PropTypes.string.isRequired,
  labelSMSUpdates: PropTypes.string.isRequired,
  labelSMSTerms: PropTypes.string.isRequired,
  headerAccountInfo: PropTypes.string.isRequired,
  labelEmail: PropTypes.string.isRequired,
  labelPassword: PropTypes.string.isRequired,
  labelConfirmation: PropTypes.string.isRequired,
  message: PropTypes.node,
  headlineBillingInformation: PropTypes.string.isRequired,
  labelWithStripe: PropTypes.string.isRequired,
  labelNameCard: PropTypes.string.isRequired,
  labelCardNumber: PropTypes.string.isRequired,
  labelExpirationDate: PropTypes.string.isRequired,
  labelCVC: PropTypes.string.isRequired,
  placeholderNameCard: PropTypes.string.isRequired,
  placeholderCardNumber: PropTypes.string.isRequired,
  placeholderExpirationDate: PropTypes.string.isRequired,
  placeholderCVC: PropTypes.string.isRequired,
  orderSummary: PropTypes.object.isRequired,
  recurringOrders: PropTypes.shape({
    foodPacksText: PropTypes.string.isRequired,
  }).isRequired,
  ctaText: PropTypes.string.isRequired,
  period: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  address: PropTypes.object.isRequired,
  updateAddress: PropTypes.func.isRequired,
  customer: PropTypes.object.isRequired,
  products: PropTypes.array.isRequired,
  subscriptionProducts: PropTypes.array.isRequired,
  cart: PropTypes.object.isRequired,
  isValidating: PropTypes.bool.isRequired,
  refreshCart: PropTypes.func.isRequired,
  deletePet: PropTypes.func.isRequired,
  pets: PropTypes.array.isRequired,
  showAddons: PropTypes.bool.isRequired,
  showPromoCode: PropTypes.bool.isRequired,
  applyPromoCode: PropTypes.func.isRequired,
  removePromoCode: PropTypes.func.isRequired,
  isDefaultPromoCode: PropTypes.bool.isRequired,
  currentOfferCode: PropTypes.string.isRequired,
  defaultOfferCode: PropTypes.string.isRequired,
};
